import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, RefreshCw, Share2, Trash2 } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import { abrirJanelaPartilhar } from '@core/partilhas/partilhasEstado'
import { recarregarModelos } from '@core/modelos/modelosPartilhados'
import { formatarBytes } from '@core/modelos/modelosRegras'
import { sessoesApi, type SessaoResumo } from './sessoesApi'
import { textoDeApagarSessao } from './sessoesRegras'
import {
  abrirPedidosDeSessao,
  esquecerSessaoActual,
  lerSessaoActual,
  sessoesMudaram,
  useVersaoDasSessoes,
} from './sessaoPartilhadaEstado'

const BOTAO =
  'flex shrink-0 items-center gap-0.5 rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

/** "Sessões partilhadas", na barra lateral, por baixo dos modelos do
 *  workspace: as sessões que a pessoa vê, com o espaço que os pontos de
 *  vista de cada uma ocupam (das suas), abrir, gerir a partilha e
 *  apagar. E os pedidos de autorização das peças dela que outras
 *  pessoas incluíram nas sessões delas. */
export default function SessoesPartilhadas() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const pushToast = useAppStore((s) => s.pushToast)
  const versao = useVersaoDasSessoes()
  const [aberto, setAberto] = useState(true)
  const [lista, setLista] = useState<SessaoResumo[] | null>(null)
  const [pedidos, setPedidos] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [aApagar, setAApagar] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    void Promise.all([sessoesApi.listar(), sessoesApi.pedidos().catch(() => [])])
      .then(([l, p]) => {
        if (!vivo) return
        setLista(l)
        setPedidos(p.length)
        setErro(null)
      })
      .catch((err) => {
        if (vivo) setErro(mensagem(err))
      })
    return () => {
      vivo = false
    }
  }, [versao, workspace?.id])

  if (!workspace || (lista !== null && lista.length === 0 && pedidos === 0 && !erro)) return null

  const apagar = async (s: SessaoResumo) => {
    const nome = s.nome ?? 'sem nome'
    if (!window.confirm(textoDeApagarSessao(nome, s.bytes, formatarBytes))) return
    setAApagar(s.id)
    try {
      await sessoesApi.apagar(s.id)
      if (lerSessaoActual(workspace.id)?.partilhaId === s.id) esquecerSessaoActual()
      pushToast(`Sessão "${nome}" enviada para a Reciclagem.`, { type: 'success' })
      sessoesMudaram()
      void recarregarModelos()
    } catch (err) {
      pushToast(`Não foi possível apagar a sessão: ${mensagem(err)}`, { type: 'error' })
    } finally {
      setAApagar(null)
    }
  }

  return (
    <div className="border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="flex items-center gap-1 uppercase"
          aria-expanded={aberto}
        >
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Sessões partilhadas ({lista?.length ?? 0})
        </button>
        <button
          type="button"
          onClick={sessoesMudaram}
          title="Atualizar a lista"
          aria-label="Atualizar a lista das sessões partilhadas"
          className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={11} />
        </button>
      </div>
      {aberto && (
        <div className="flex max-h-56 flex-col gap-1.5 overflow-y-auto px-3 pb-2 text-xs">
          {erro && <p className="text-[var(--color-error-text)]">{erro}</p>}
          {pedidos > 0 && (
            <button
              type="button"
              onClick={abrirPedidosDeSessao}
              className="rounded border border-[var(--color-accent-border)] px-1.5 py-1 text-left text-[11px] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10"
            >
              {pedidos === 1
                ? '1 pedido de autorização: uma peça sua numa sessão de outra pessoa'
                : `${pedidos} pedidos de autorização: peças suas em sessões de outras pessoas`}
            </button>
          )}
          {(lista ?? []).map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-1 rounded border border-[var(--color-border-subtle)] px-1.5 py-1"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[var(--color-text)]" title={s.nome ?? ''}>
                  {s.nome ?? 'Sessão sem nome'}
                </div>
                <div className="truncate text-[10px] text-[var(--color-text-dim)]">
                  {s.pode_gerir ? 'partilhada por si' : `de ${s.dono_nome ?? 'outra pessoa'}`}
                  {s.pode_gerir && s.bytes > 0 && ` · ${formatarBytes(s.bytes)} de pontos de vista`}
                </div>
                {s.por_autorizar > 0 && (
                  <div className="truncate text-[10px] text-[var(--color-warn-text)]">
                    {s.por_autorizar === 1
                      ? '1 peça à espera da autorização do dono'
                      : `${s.por_autorizar} peças à espera da autorização dos donos`}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/${workspace.slug}/?sessao=${encodeURIComponent(s.id)}`)}
                title="Abrir esta sessão (substitui a sessão actual; pergunta antes se houver alguma coisa aberta)"
                className={BOTAO}
              >
                Abrir
              </button>
              <button
                type="button"
                onClick={() =>
                  abrirJanelaPartilhar({
                    tipo: 'sessao',
                    nome: s.nome ?? 'Sessão',
                    partilhaId: s.id,
                    workspaceNome: workspace.name,
                    aoMudar: () => {
                      sessoesMudaram()
                      void recarregarModelos()
                    },
                  })
                }
                title={s.pode_gerir ? 'Com quem está partilhada esta sessão' : 'Quem tem acesso a esta sessão'}
                aria-label={`${s.pode_gerir ? 'Gerir a partilha de' : 'Acessos a'} ${s.nome ?? 'sessão'}`}
                className={BOTAO}
              >
                <Share2 size={10} aria-hidden />
                {s.pode_gerir ? 'Gerir' : 'Acessos'}
              </button>
              {s.pode_gerir && (
                <button
                  type="button"
                  disabled={aApagar === s.id}
                  onClick={() => void apagar(s)}
                  title="Apagar a sessão partilhada: tira os acessos que ela deu e liberta o espaço dos pontos de vista. Fica 7 dias na Reciclagem, onde se pode restaurar. Os modelos, a triagem e os BCF ficam."
                  aria-label={`Apagar a sessão ${s.nome ?? ''}`}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--color-text-dim)] hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)] disabled:opacity-40"
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
